import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { HttpResponse } from '@tf/api';
import { gql, GraphQLClient, PublicApiClient } from '@tf/api';
import { objectRelationValue } from '@tf/utils';

import type { PaginationParams, SortingParams, SortingType } from '@/core/api/payments';
import { getSchemaUrl } from '@/core/api/schemas';
import { buildCompoundFilter, type FilteringParams } from '@/core/api/utils/buildCompoundFilter';
import type { CompoundFilter } from '@/core/api/utils/typeUtils';
import type { ProfileConnectionSchema, SchemaResolveResponse } from '@/core/types/schemas';

const profileConnectionsSchemasQueryOptions = () => {
	const PROFILE_CONNECTION_PATH = 'types/profile_connection/connections';

	return queryOptions({
		queryKey: ['connectionsFromSchemas', 'all'],
		queryFn: async (): Promise<ProfileConnectionSchema | null> => {
			const api = PublicApiClient.getInstance();

			const resolveResp: HttpResponse<SchemaResolveResponse[]> = await api.request({
				path: `/sl/schemas/public/schema/resolve`,
				method: 'POST',
				body: [PROFILE_CONNECTION_PATH],
			});

			const resolved: SchemaResolveResponse[] = await resolveResp.json();

			const schemaId = resolved?.[0]?.resolved_schemas.map(({ id }) => id)[0];

			if (!schemaId) {
				console.log('No schema found for profile connections, this is expected in some environments');
				return null;
			}

			const schemaResp = await api.request({
				path: `/sl/schemas/public/schema_by_uri?uri=${encodeURIComponent(schemaId)}`,
				method: 'GET',
			});

			return await schemaResp.json();
		},
		staleTime: Infinity,
	});
};

export const useProfileConnectionsSchemas = () => {
	return useQuery(profileConnectionsSchemasQueryOptions());
};

type ProfileConnectionsResponse = {
	id: string;
	meta: { '@createdAt': string };
	profile_a: {
		object: {
			id: string;
			labels: { name: string };
		};
	}[];
	profile_b: {
		object: {
			id: string;
			labels: { name: string };
		};
	}[];
}[];

const extractOppositeProfiles = (connections: ProfileConnectionsResponse, currentProfileId: string) => {
	return connections.flatMap((connection) => {
		const profileAObjects = connection.profile_a.map((rel) => rel.object);
		const profileBObjects = connection.profile_b.map((rel) => rel.object);

		const objectsToReturn = profileAObjects.some((obj) => obj.id === currentProfileId)
			? profileBObjects
			: profileAObjects;

		return objectsToReturn.map((obj) => ({
			id: obj.id,
			connectionId: connection.id,
			createdAt: connection.meta['@createdAt'],
			name: obj.labels.name,
		}));
	});
};

export const profileConnectionsQueryOptions = (profileId: string) =>
	queryOptions({
		queryKey: ['profileConnections', profileId],
		queryFn: async (): Promise<{ get: ProfileConnectionsResponse }> => {
			return GraphQLClient.getInstance().request<
				{ get: ProfileConnectionsResponse },
				{ profileId: string }
			>(
				gql`
					query GetProfileConnections($profileId: JSON!) {
						get(
							rootFilter: {
								archetype: "profile_connection"
								meta: {
									AND: [
										{
											OR: [
												{ key: "/@with/profile_a", operator: EQ, value: $profileId }
												{ key: "/@with/profile_b", operator: EQ, value: $profileId }
											]
										}
										{ key: "status", operator: EQ, value: "ACTIVE" }
									]
								}
							}
							paginationFilter: { sortByMeta: "/@createdAt", sortByDirection: DESC }
						) {
							id
							meta(path: ["/@createdAt"])
							profile_a: nestedRelation(filter: { AND: { relationKind: "profile_a" } }) {
								object {
									id
									labels(path: ["/name"])
								}
							}
							profile_b: nestedRelation(filter: { AND: { relationKind: "profile_b" } }) {
								object {
									id
									labels(path: ["/name"])
								}
							}
						}
					}
				`,
				{ profileId }
			);
		},
		select: ({ get }) => extractOppositeProfiles(get, profileId),
	});

export const useProfileConnections = (profileId: string) => {
	return useQuery(profileConnectionsQueryOptions(profileId));
};

type ProfileListItem = { id: string; kind: string; labels: { name: string } };

export const profilesLookupQueryOptions = (kinds: string[], query: string) =>
	queryOptions({
		queryKey: ['profilesByKind', kinds, query],
		queryFn: async (): Promise<{ get: ProfileListItem[] }> => {
			return GraphQLClient.getInstance().request<
				{ get: ProfileListItem[] },
				{ kinds: string[]; query: string }
			>(
				gql`
					query GetProfilesByKind($kinds: [String]!, $query: JSON!) {
						get(
							rootFilter: {
								archetype: "profile"
								kinds: $kinds
								labels: { AND: [{ key: "/name", operator: CONTAINS, value: $query }] }
							}
							paginationFilter: { sortByMeta: "/@createdAt", sortByDirection: DESC }
						) {
							id
							kind
							labels(path: ["/name"])
						}
					}
				`,
				{ kinds, query }
			);
		},
		select: ({ get }) => get,
	});

export const useProfilesLookupQuery = (kinds: string[], query: string) => {
	return useQuery(profilesLookupQueryOptions(kinds, query));
};

interface CreateConnectionVars {
	profileAId: string;
	profileBId: string;
}

export const useCreateConnectionMutation = () => {
	const schema = getSchemaUrl('types/profile_connection/partnership');
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ profileAId, profileBId }: CreateConnectionVars) => {
			return GraphQLClient.getInstance().request(
				gql`
					mutation CreateConnection($create: [ObjectInput!]!) {
						mutate(create: $create) {
							id
							kind
							archetype
							meta
						}
					}
				`,
				{
					create: [
						{
							id: 'new://profile_connection',
							schema,
							data: {
								profile_a: objectRelationValue('profile_a', profileAId),
								profile_b: objectRelationValue('profile_b', profileBId),
							},
							meta: {
								status: 'ACTIVE',
							},
						},
					],
				}
			);
		},
		onSuccess: async (_, { profileAId }) => {
			return queryClient.invalidateQueries(profileConnectionsQueryOptions(profileAId));
		},
	});
};

interface RemoveConnectionVars {
	connectionId: string;
	profileId: string;
}

export const useRemoveConnectionMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ connectionId }: RemoveConnectionVars) => {
			return GraphQLClient.getInstance().request(
				gql`
					mutation RemoveConnection($patch: [PatchInput!]!) {
						mutate(patch: $patch) {
							id
							kind
							archetype
							meta
						}
					}
				`,
				{
					patch: [
						{
							objectId: connectionId,
							objectMetaPatch: [
								{
									op: 'replace',
									path: '/status',
									value: 'REMOVED',
								},
							],
						},
					],
				}
			);
		},
		onSuccess: async (_, { profileId }) => {
			return queryClient.invalidateQueries(profileConnectionsQueryOptions(profileId));
		},
	});
};

export type ProfileGroups = {
	label: string;
	kinds: string[];
	order: string;
};

type GroupKindsDefinition = {
	type: 'object';
	properties: {
		kinds: {
			type: 'array';
			items: {
				enum: string[];
				type: 'string';
			};
		};
		order: {
			const: string;
		};
	};
};

interface ProfileGroupsSchemaResponse {
	json_schema: {
		$defs: Record<string, GroupKindsDefinition>;
		$id: string;
	};
	ui_schema: Record<string, unknown>;
	idempotency_key: string;
}

export const profileGroupsSchemasQueryOptions = () => {
	const PROFILE_GROUPS_PATH = 'types/profile_groups/groups';

	return queryOptions({
		queryKey: ['groupsFromSchemas', 'all'],
		queryFn: async (): Promise<{ groups: ProfileGroups[] } | null> => {
			try {
				const api = PublicApiClient.getInstance();

				const resolveResp: HttpResponse<SchemaResolveResponse[]> = await api.request({
					path: `/sl/schemas/public/schema/resolve`,
					method: 'POST',
					body: [PROFILE_GROUPS_PATH],
				});

				const resolved: SchemaResolveResponse[] = await resolveResp.json();
				const schemaId = resolved?.[0]?.resolved_schemas.map(({ id }) => id)[0];

				if (!schemaId) {
					console.log('No schema found for profile groups, this is expected in some environments');
					return null;
				}

				const schemaResp = await api.request({
					path: `/sl/schemas/public/schema_by_uri?uri=${encodeURIComponent(schemaId)}`,
					method: 'GET',
				});

				const schemaData: ProfileGroupsSchemaResponse = await schemaResp.json();

				const groups: ProfileGroups[] = Object.entries(schemaData.json_schema.$defs)
					.map(([label, definition]) => ({
						label,
						kinds: definition.properties.kinds.items.enum,
						order: definition.properties.order.const,
					}))
					.sort((a, b) => Number(a.order) - Number(b.order));

				return { groups };
			} catch (error) {
				return null;
			}
		},
		staleTime: Infinity,
	});
};

export const useProfileGroupsSchemas = () => {
	return useQuery(profileGroupsSchemasQueryOptions());
};

export type Profile = {
	id: string;
	kind: string;
	labels: {
		name?: string;
		assigned_managers?: string[];
		priority?: string;
	};
	meta: {
		status: string;
		status_changed_at: string;
		next_kyc_review_date?: string;
		risk_level?: string;
	};
};

const profileSearchLabels = ['name'];
const metaFilterPaths = ['/status'];
const labelFilterPaths = ['/assigned_managers', '/priority'];

const allProfilesOptions = (
	kinds: string[],
	pagination?: PaginationParams,
	sorting?: SortingParams,
	filtering?: FilteringParams,
	search?: string
) =>
	queryOptions({
		queryKey: ['profiles', 'all', kinds, pagination, sorting, filtering, search],
		queryFn: async () => {
			const metaFiltering: FilteringParams = filtering
				? filtering.filter((f) => metaFilterPaths.includes(f.path))
				: [];
			const labelFiltering: FilteringParams = filtering
				? filtering.filter((f) => labelFilterPaths.includes(f.path))
				: [];

			const hasArchivedFilter = metaFiltering.some(
				(f) => f.path === '/status' && f.value === 'ARCHIVED'
			);

			if (!hasArchivedFilter) {
				metaFiltering.push({ path: '/status', value: 'ARCHIVED', operator: 'NEQ' });
			}

			const metaCompoundFilter = buildCompoundFilter(metaFiltering, '', []);
			const labelCompoundFilter = buildCompoundFilter(labelFiltering, search, profileSearchLabels);

			return GraphQLClient.getInstance().requestSafe<
				{ get: Profile[] },
				{
					kinds: string[];
					offset: number;
					limit: number;
					sortByLabels?: string;
					sortByMeta?: string;
					sortByType?: SortingType;
					sortByDirection?: 'ASC' | 'DESC';
					labelsFilter?: CompoundFilter;
					metaFilter?: CompoundFilter;
				}
			>(
				gql`
					query AllProfiles(
						$kinds: [String!]!
						$offset: Int!
						$limit: Int!
						$sortByLabels: JSONPointer
						$sortByMeta: JSONPointer
						$sortByType: SortType
						$sortByDirection: SortDirection
						$labelsFilter: KeyValueFilter
						$metaFilter: KeyValueFilter
					) {
						get(
							rootFilter: { kinds: $kinds, labels: $labelsFilter, meta: $metaFilter }
							paginationFilter: {
								offset: $offset
								limit: $limit
								sortByLabels: $sortByLabels
								sortByMeta: $sortByMeta
								sortByType: $sortByType
								sortByDirection: $sortByDirection
							}
						) {
							id
							labels(path: ["/name", "/assigned_managers", "/priority"])
							kind
							meta(path: ["/status", "/status_changed_at", "/next_kyc_review_date", "/risk_level"])
						}
					}
				`,
				{
					kinds,
					offset: pagination?.offset ?? 0,
					limit: pagination?.limit ?? 10,
					sortByLabels: sorting?.sortByLabels,
					sortByMeta: sorting?.sortByMeta,
					sortByType: sorting?.sortByType,
					sortByDirection: sorting?.sortByDirection,
					labelsFilter: labelCompoundFilter,
					metaFilter: metaCompoundFilter,
				}
			);
		},
	});

export const useAllProfiles = (
	kinds: string[],
	pagination?: PaginationParams,
	sorting?: SortingParams,
	filtering?: FilteringParams,
	search?: string
) => {
	return useQuery(allProfilesOptions(kinds, pagination, sorting, filtering, search));
};

export const useSetProfileAssigneesMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ profileId, assignees }: { profileId: string; assignees: string[] }) => {
			return GraphQLClient.getInstance().request(
				gql`
					mutation SetProfileAssignees($patch: [PatchInput!]!) {
						mutate(patch: $patch) {
							id
						}
					}
				`,
				{
					patch: [
						{
							objectId: profileId,
							objectLabelsPatch: [{ op: 'add', path: '/assigned_managers', value: assignees }],
						},
					],
				}
			);
		},
		onSuccess: () => {
			return queryClient.invalidateQueries({ queryKey: ['profiles'] });
		},
	});
};

export const useSetProfilePriorityMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ profileId, priority }: { profileId: string; priority: string }) => {
			return GraphQLClient.getInstance().request(
				gql`
					mutation SetProfilePriority($patch: [PatchInput!]!) {
						mutate(patch: $patch) {
							id
						}
					}
				`,
				{
					patch: [
						{
							objectId: profileId,
							objectLabelsPatch: [{ op: 'add', path: '/priority', value: priority }],
						},
					],
				}
			);
		},
		onSuccess: () => {
			return queryClient.invalidateQueries({ queryKey: ['profilesByKinds'] });
		},
	});
};
